import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import mermaid from "mermaid";
import {
  Send,
  ArrowLeft,
  Scale,
  Trash2,
  Sparkles,
  MessageSquare,
  PiggyBank,
  Home,
  TrendingUp,
  Receipt,
  BarChart3,
  Loader2,
  Calculator,
  FileText,
  Menu,
  X,
  ChevronRight,
  Paperclip,
  Share2,
  FileUp,
  Image as ImageIcon,
  User,
  LogOut,
  Crown,
  Zap,
  History,
  Heart,
  Languages,
  Globe,
  Copy,
  Check,
  Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import ThemeToggle from "../components/ThemeToggle";
import AuthModal from "../components/AuthModal";
import FeedbackModal from "../components/FeedbackModal";
import QueryLimitModal from "../components/QueryLimitModal";
import InlineCalcForm from "../components/InlineCalcForm";
import { API_BASE_URL, API_URL } from "../utils/api";

const BACKEND_URL = API_BASE_URL;
const API = API_URL;

// Mermaid will be initialized dynamically based on theme inside component

const ChatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Theme Context for Mermaid
  const { theme } = useTheme();
  
  // Auth Context
  const { user, isAuthenticated, logout, checkQueryLimit, incrementQueryCount, getUsageInfo, loginWithGoogle, loginWithMobile, continueAsGuest, funnelState } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTrigger, setAuthModalTrigger] = useState('limit');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [showQueryLimitModal, setShowQueryLimitModal] = useState(false);
  const [queryLimitType, setQueryLimitType] = useState('login_wall');
  const [pendingQuery, setPendingQuery] = useState(null);
  const [modalQueryInfo, setModalQueryInfo] = useState({ used: 0, max: 1 });
  const [isGuestMode, setIsGuestMode] = useState(() => {
    return localStorage.getItem('taxsetu_guest_mode') === 'true';
  });

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("taxsetu_session") || null;
  });
  const [quickQueries, setQuickQueries] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [recentChats, setRecentChats] = useState([]);
  const [translatingIndex, setTranslatingIndex] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [messageLanguages, setMessageLanguages] = useState({});

  // Get current usage info for display
  const usageInfo = getUsageInfo();

  // Fetch recent chats
  const fetchRecentChats = async () => {
    try {
      const url = user?.email 
        ? `${API}/chat-sessions?user_email=${encodeURIComponent(user.email)}`
        : `${API}/chat-sessions`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setRecentChats(data.sessions || []);
      }
    } catch (error) {
      console.log('Could not fetch recent chats');
    }
  };

  // Initialize Mermaid with theme-aware configuration
  useEffect(() => {
    const isDark = theme === 'dark';
    mermaid.initialize({
      startOnLoad: false,
      theme: isDark ? 'dark' : 'neutral',
      securityLevel: 'loose',
      themeVariables: isDark ? {
        primaryColor: '#06b6d4',
        primaryTextColor: '#fff',
        primaryBorderColor: '#0891b2',
        lineColor: '#64748b',
        secondaryColor: '#1e293b',
        tertiaryColor: '#0f172a',
        background: '#0A1A30',
        mainBkg: '#1e293b',
        nodeBorder: '#06b6d4',
        clusterBkg: '#1e293b',
        titleColor: '#f1f5f9',
        edgeLabelBackground: '#1e293b'
      } : {
        primaryColor: '#0891b2',
        primaryTextColor: '#1e293b',
        primaryBorderColor: '#06b6d4',
        lineColor: '#64748b',
        secondaryColor: '#f1f5f9',
        tertiaryColor: '#e2e8f0',
        background: '#ffffff',
        mainBkg: '#f8fafc',
        nodeBorder: '#0891b2',
        clusterBkg: '#f1f5f9',
        titleColor: '#1e293b',
        edgeLabelBackground: '#f8fafc'
      }
    });
  }, [theme]);

  useEffect(() => {
    fetchQuickQueries();
    fetchRecentChats();
  }, [user?.email]);

  useEffect(() => {
    if (location.state?.initialQuery) {
      setInputValue(location.state.initialQuery);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const fetchQuickQueries = async () => {
    try {
      const response = await axios.get(`${API}/quick-queries`);
      setQuickQueries(response.data.queries);
    } catch (error) {
      console.error("Failed to fetch quick queries:", error);
    }
  };

  const getIconForQuery = (iconName) => {
    const icons = {
      "scale": <Scale className="w-4 h-4" />,
      "chart": <BarChart3 className="w-4 h-4" />,
      "piggy-bank": <PiggyBank className="w-4 h-4" />,
      "home": <Home className="w-4 h-4" />,
      "trending-up": <TrendingUp className="w-4 h-4" />,
      "receipt": <Receipt className="w-4 h-4" />
    };
    return icons[iconName] || <MessageSquare className="w-4 h-4" />;
  };

  // State for image preview
  const [imagePreview, setImagePreview] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        'image/webp'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or image file (PNG, JPG, GIF, WebP)");
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("File size should be less than 10MB");
        return;
      }
      
      setUploadedFile(file);
      
      // Generate preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
        toast.success(`Image "${file.name}" attached`);
      } else {
        setImagePreview(null);
        toast.success(`PDF "${file.name}" attached`);
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle paste from clipboard (Ctrl+V)
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if the pasted item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent default paste behavior
        
        const file = item.getAsFile();
        if (file) {
          if (file.size > 10 * 1024 * 1024) {
            toast.error("Image size should be less than 10MB");
            return;
          }
          
          // Create a named file from clipboard
          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
          const namedFile = new File([file], `screenshot_${timestamp}.png`, { type: file.type });
          
          setUploadedFile(namedFile);
          
          // Generate preview
          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreview(reader.result);
          };
          reader.readAsDataURL(file);
          
          toast.success("Screenshot pasted! Press Enter to send.");
        }
        break;
      }
    }
  };

  // Detect language of user query - DEFAULT TO ENGLISH ALWAYS
  const detectQueryLanguage = (query) => {
    // Always return English by default
    // User can switch to other languages using the toggle after seeing the response
    return 'english';
  };

  // Classify query as general or specific
  const classifyQuery = (query) => {
    const generalPatterns = [
      /^(what is|kya hai|define|definition|explain|samjhao).*(income tax|tds|itr|gst|tax)\??$/i,
      /^(who needs to|kaun|kisko).*(file|return|pay)/i,
      /^(hi|hello|hey|namaste|good morning|good evening)/i,
    ];
    
    const isGeneral = generalPatterns.some(pattern => pattern.test(query.toLowerCase().trim()));
    return isGeneral ? 'general' : 'specific';
  };

  const sendMessage = async (message = inputValue) => {
    if ((!message.trim() && !uploadedFile) || isLoading) return;

    // Check query limit using new funnel logic
    const limitCheck = await checkQueryLimit('technical', message);
    
    if (!limitCheck.allowed) {
      // Store the pending query to send after login
      setPendingQuery(message);
      
      // Store the query info for modal display
      setModalQueryInfo({
        used: limitCheck.queriesUsed || 0,
        max: limitCheck.maxQueries || 1
      });
      
      if (limitCheck.action === 'login_wall') {
        setQueryLimitType('login_wall');
        setShowQueryLimitModal(true);
        return;
      } else if (limitCheck.action === 'force_login') {
        setQueryLimitType('force_login');
        setShowQueryLimitModal(true);
        return;
      } else if (limitCheck.action === 'paywall') {
        setQueryLimitType('paywall');
        setShowQueryLimitModal(true);
        return;
      }
    }

    let userMessageContent = message.trim();
    let userMessageImage = null;
    
    if (uploadedFile) {
      if (uploadedFile.type.startsWith('image/')) {
        userMessageContent = `[Image Attached: ${uploadedFile.name}] ${userMessageContent}`;
        userMessageImage = imagePreview;
      } else {
        userMessageContent = `[PDF Attached: ${uploadedFile.name}] ${userMessageContent}`;
      }
    }

    // Upload attachment to backend FIRST so admin has visibility (and we get URL/extracted text)
    let attachmentsMeta = [];
    if (uploadedFile) {
      try {
        const fd = new FormData();
        fd.append('file', uploadedFile);
        fd.append('session_id', sessionId || '');
        fd.append('user_email', user?.email || '');
        const res = await fetch(`${API}/chat/upload-attachment`, { method: 'POST', body: fd });
        if (res.ok) {
          const meta = await res.json();
          attachmentsMeta = [meta];
        }
      } catch (err) {
        console.warn('Attachment upload failed (chat will continue without admin record):', err);
      }
    }

    // Detect user's query language
    const detectedLanguage = detectQueryLanguage(message);

    const userMessage = { 
      role: "user", 
      content: userMessageContent,
      image: userMessageImage,
      language: detectedLanguage
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setLoadingStatus('Thinking…');
    setIsSidebarOpen(false);

    // Progressive loading status messages — neutral, IT Act 2025 first
    const loadingMessages = [
      { delay: 1500, text: 'Looking up the provisions…' },
      { delay: 3500, text: 'Preparing your answer…' },
      { delay: 7000, text: 'Almost there…' },
    ];

    const statusTimers = loadingMessages.map(({ delay, text }) => 
      setTimeout(() => setLoadingStatus(text), delay)
    );

    // Add empty assistant message that will be filled by streaming - with detected language
    const assistantMessageIndex = messages.length + 1;
    setMessages(prev => [...prev, { role: "assistant", content: "", currentLanguage: detectedLanguage }]);

    try {
      let messageToSend = message;
      
      if (uploadedFile) {
        if (uploadedFile.type.startsWith('image/')) {
          if (message.trim()) {
            // User provided a specific question about the image
            messageToSend = `User has attached an image named "${uploadedFile.name}". Their question about this image: ${message}`;
          } else {
            // No query provided - ask user what they want to know with attractive formatting
            messageToSend = `User has attached an image named "${uploadedFile.name}" but hasn't asked a specific question. Please respond EXACTLY with this formatted message:

*I see you've attached an image!* 👀

What would you like me to help you with?

**Here are some things I can do:**

> 📄 *Analyze this document for tax implications*

> 🔍 *Extract key information from this notice*

> 💰 *Check if any TDS deductions apply*

> 📋 *Identify the type of tax form or notice*

*Just type your question and I'll help you out!*`;
          }
        } else {
          if (message.trim()) {
            messageToSend = `User has attached a PDF document named "${uploadedFile.name}". Their question: ${message}`;
          } else {
            messageToSend = `User has attached a PDF document named "${uploadedFile.name}" but hasn't asked a specific question. Please respond EXACTLY with this formatted message:

*I see you've attached a PDF document!* 📄

What would you like me to help you with?

**Here are some things I can do:**

> 📝 *Summarize this document*

> 🔍 *Check for tax-related information*

> 📅 *Identify key dates or amounts*

> 📋 *Analyze any notices or forms*

*Just type your question and I'll help you out!*`;
          }
        }
      }

      // Map frontend language values to backend expected values
      const languageMap = {
        'english': 'en',
        'hinglish': 'hi-en',
        'hindi': 'hi'
      };
      const backendLanguage = languageMap[detectedLanguage] || 'en';

      // Try streaming first, fall back to regular endpoint if streaming doesn't work
      let useStreaming = true;
      let fullResponse = "";
      
      try {
        // Use streaming endpoint for real-time response
        const response = await fetch(`${API}/chat-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify({
            message: messageToSend,
            session_id: sessionId,
            user_email: user?.email || '',
            language: backendLanguage,
            attachments: attachmentsMeta,
          })
        });

        if (!response.ok) {
          throw new Error('Stream request failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let receivedData = false;

        // Set a timeout to check if we're receiving data
        const streamTimeout = setTimeout(() => {
          if (!receivedData) {
            console.log("Streaming not receiving data, may be buffered");
          }
        }, 3000);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          receivedData = true;
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete SSE events from buffer
          const events = buffer.split('\n\n');
          buffer = events.pop() || "";

          for (const event of events) {
            const lines = event.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  if (data.error) {
                    clearTimeout(streamTimeout);
                    toast.error("AI service error. Please try again.");
                    setMessages(prev => prev.slice(0, -1));
                    setIsLoading(false);
                    return;
                  }
                  
                  if (data.text) {
                    fullResponse += data.text;
                    setMessages(prev => {
                      const newMessages = [...prev];
                      newMessages[newMessages.length - 1] = {
                        role: "assistant",
                        content: fullResponse,
                        currentLanguage: detectedLanguage
                      };
                      return newMessages;
                    });
                  }
                  
                  if (data.done && data.session_id) {
                    if (!sessionId) {
                      setSessionId(data.session_id);
                      localStorage.setItem("taxsetu_session", data.session_id);
                    }
                  }
                } catch (e) {
                  console.log("Parse error:", e);
                }
              }
            }
          }
        }
        clearTimeout(streamTimeout);
        
      } catch (streamError) {
        console.log("Streaming failed, using regular endpoint:", streamError);
        useStreaming = false;
      }

      // If streaming didn't produce results, fall back to regular endpoint
      if (!fullResponse) {
        const response = await fetch(`${API}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: messageToSend,
            session_id: sessionId,
            user_email: user?.email || '',
            language: backendLanguage,
            attachments: attachmentsMeta,
          })
        });
        
        if (!response.ok) {
          throw new Error('Request failed');
        }
        
        const data = await response.json();
        fullResponse = data.response;
        
        if (!sessionId && data.session_id) {
          setSessionId(data.session_id);
          localStorage.setItem("taxsetu_session", data.session_id);
        }
        
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            role: "assistant",
            content: fullResponse,
            currentLanguage: detectedLanguage
          };
          return newMessages;
        });
      }

      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      // Log query for analytics and increment usage count
      const queryType = classifyQuery(message);
      await incrementQueryCount(queryType, message, fullResponse, sessionId || 'new-session');
      
      // After the first query, show login modal for non-logged-in users
      if (!user) {
        const limitCheck = await checkQueryLimit('technical', message);
        if (!limitCheck.allowed) {
          setAuthModalTrigger('limit');
          setModalQueryInfo({
            used: limitCheck.queriesUsed || 1,
            max: limitCheck.maxQueries || 1
          });
          // Show modal after a short delay so user can see the response first
          setTimeout(() => {
            setShowAuthModal(true);
          }, 2000);
        }
      }
      
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to get response. Please try again.");
      setMessages(prev => prev.slice(0, -2)); // Remove both user and empty assistant message
    } finally {
      // Clear all status timers
      statusTimers.forEach(timer => clearTimeout(timer));
      setIsLoading(false);
      setLoadingStatus('');
    }
  };

  const shareToWhatsApp = (message) => {
    const text = encodeURIComponent(`*TaxSetu AI Response:*\n\n${message}\n\n_Get your tax queries answered at TaxSetu.ai_`);
    const url = `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  // Copy AI response to clipboard
  const copyToClipboard = async (content, index) => {
    if (!content) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(content);
      } else {
        // Fallback for non-secure contexts
        const textarea = document.createElement('textarea');
        textarea.value = content;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedIndex(index);
      toast.success('Answer copied to clipboard');
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      toast.error('Failed to copy answer');
    }
  };

  // Translate AI response to different language
  const translateResponse = async (messageIndex, targetLanguage) => {
    const message = messages[messageIndex];
    if (!message || message.role !== 'assistant') return;

    // Check if we already have this translation cached
    const cacheKey = `${messageIndex}-${targetLanguage}`;
    if (messageLanguages[cacheKey]) {
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[messageIndex] = {
          ...newMessages[messageIndex],
          content: messageLanguages[cacheKey],
          currentLanguage: targetLanguage
        };
        return newMessages;
      });
      return;
    }

    setTranslatingIndex(messageIndex);

    try {
      const response = await fetch(`${API}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: message.originalContent || message.content,
          target_language: targetLanguage
        })
      });

      if (response.ok) {
        const data = await response.json();
        const translatedText = data.translated_text;

        // Cache the translation
        setMessageLanguages(prev => ({
          ...prev,
          [cacheKey]: translatedText,
          [`${messageIndex}-original`]: message.originalContent || message.content
        }));

        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[messageIndex] = {
            ...newMessages[messageIndex],
            originalContent: newMessages[messageIndex].originalContent || newMessages[messageIndex].content,
            content: translatedText,
            currentLanguage: targetLanguage
          };
          return newMessages;
        });
      } else {
        toast.error('Translation failed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Translation failed');
    } finally {
      setTranslatingIndex(null);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/chat-history/${sessionId}`);
      } catch (error) {
        console.error("Failed to clear history:", error);
      }
    }
    setMessages([]);
    localStorage.removeItem("taxsetu_session");
    setSessionId(null);
    toast.success("Chat cleared!");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Parse markdown table to HTML table
  const parseMarkdownTable = (lines) => {
    if (lines.length < 2) return null;
    
    const headerLine = lines[0];
    const separatorLine = lines[1];
    const dataLines = lines.slice(2);
    
    // Check if it's a valid table
    if (!separatorLine.match(/^\|[\s-:|]+\|$/)) return null;
    
    // Parse header
    const headers = headerLine.split('|').filter(h => h.trim()).map(h => h.trim());
    
    // Parse data rows
    const rows = dataLines.map(line => 
      line.split('|').filter(c => c.trim() !== '').map(c => c.trim())
    ).filter(row => row.length > 0);
    
    // Build HTML table
    let html = '<div class="overflow-x-auto my-3"><table class="w-full border-collapse text-sm">';
    html += '<thead><tr class="bg-slate-800/50 border-b border-slate-600">';
    headers.forEach(h => {
      const formattedH = h.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      html += `<th class="px-3 py-2 text-left text-white font-semibold">${formattedH}</th>`;
    });
    html += '</tr></thead>';
    html += '<tbody>';
    rows.forEach((row, idx) => {
      const bgClass = idx % 2 === 0 ? 'bg-slate-900/30' : 'bg-slate-800/20';
      html += `<tr class="${bgClass} border-b border-slate-700/50 hover:bg-slate-700/30">`;
      row.forEach(cell => {
        const formattedCell = cell.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
        html += `<td class="px-3 py-2 text-slate-200">${formattedCell}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table></div>';
    
    return html;
  };

  // ── Interactive option chips: parse [[CHIPS: opt1 | opt2 | opt3]] ────────
  const CHIP_REGEX = /\[\[\s*CHIPS?\s*:\s*([^\]]+)\]\]/i;
  const FORM_REGEX = /\[\[\s*FORM\s*:\s*([a-zA-Z0-9_\-]+)\s*\]\]/i;
  const extractChips = (content) => {
    if (!content) return [];
    const m = content.match(CHIP_REGEX);
    if (!m) return [];
    return m[1]
      .split("|")
      .map((s) => s.trim().replace(/^`|`$/g, "").trim())
      .filter((s) => s.length > 0 && s.length <= 60)
      .slice(0, 3);
  };
  const extractFormId = (content) => {
    if (!content) return null;
    const m = content.match(FORM_REGEX);
    return m ? m[1].toLowerCase() : null;
  };
  const stripChips = (content) => {
    if (!content) return content;
    return content
      .replace(CHIP_REGEX, "")
      .replace(FORM_REGEX, "")
      .replace(/`\s*`/g, "")
      .trim();
  };

  const formatMessage = (content) => {
    // Check for Mermaid diagrams and process them
    const mermaidRegex = /```mermaid\s*([\s\S]*?)```/gi;
    let mermaidCounter = 0;
    
    // Replace mermaid code blocks with placeholder divs
    content = content.replace(mermaidRegex, (match, code) => {
      const id = `mermaid-${Date.now()}-${mermaidCounter++}`;
      // Schedule mermaid rendering after DOM update
      setTimeout(async () => {
        try {
          const element = document.getElementById(id);
          if (element) {
            const { svg } = await mermaid.render(`mermaid-svg-${id}`, code.trim());
            element.innerHTML = svg;
          }
        } catch (e) {
          console.error('Mermaid render error:', e);
          const element = document.getElementById(id);
          if (element) {
            element.innerHTML = `<pre class="text-red-400 text-sm">Flowchart rendering failed. Code:\n${code}</pre>`;
          }
        }
      }, 100);
      return `<div id="${id}" class="mermaid-container my-4 p-4 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-cyan-500/30 overflow-x-auto flex justify-center"><div class="animate-pulse text-cyan-600 dark:text-cyan-400">Loading flowchart...</div></div>`;
    });
    
    const lines = content.split('\n');
    let result = [];
    let tableBuffer = [];
    let inTable = false;
    
    // First, process inline citations
    const processCitations = (text) => {
      // IT Rules 2026 citations: [📄 Page X, Rule Y] - Convert to simple text reference
      text = text.replace(/\[📄\s*Page\s*(\d+),?\s*Rule\s*([^\]]+)\]/gi, (match, page, rule) => {
        return `<span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/15 border border-emerald-500/30 rounded text-emerald-400 text-xs font-medium">
          P.${page}, R.${rule}
        </span>`;
      });
      
      // IT Act 2025 citations: [📘 Section X] - Convert to simple text reference
      text = text.replace(/\[📘\s*Section\s*([^\]]+)\]/gi, (match, section) => {
        return `<span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-500/15 border border-cyan-500/30 rounded text-cyan-400 text-xs font-medium">
          Sec ${section}
        </span>`;
      });
      
      // IT Act 1961 citations: [📙 Section X] - Convert to simple text reference
      text = text.replace(/\[📙\s*Section\s*([^\]]+)\]/gi, (match, section) => {
        return `<span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/15 border border-amber-500/30 rounded text-amber-400 text-xs font-medium">
          Sec ${section}
        </span>`;
      });
      
      return text;
    };
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Check if this is a table line
      const isTableLine = line.trim().startsWith('|') && line.trim().endsWith('|') && !line.includes('http');
      
      if (isTableLine) {
        inTable = true;
        tableBuffer.push(line);
      } else {
        // If we were in a table, process it
        if (inTable && tableBuffer.length > 0) {
          const tableHtml = parseMarkdownTable(tableBuffer);
          if (tableHtml) {
            result.push(tableHtml);
          } else {
            // Fallback to monospace display
            tableBuffer.forEach(tl => {
              result.push(`<p class="my-0 font-mono text-sm text-white">${tl}</p>`);
            });
          }
          tableBuffer = [];
          inTable = false;
        }
        
        // Process regular line
        // Remove any inline color styles
        line = line.replace(/style="[^"]*color[^"]*"/gi, '');
        // Process inline citations FIRST
        line = processCitations(line);
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
        // Italic text
        line = line.replace(/\*(.*?)\*/g, '<em class="text-slate-100 italic">$1</em>');
        // Emojis - subtle
        line = line.replace(/⚠️/g, '<span class="text-slate-300">⚠️</span>');
        line = line.replace(/⚡/g, '<span class="text-slate-300">⚡</span>');
        line = line.replace(/📋/g, '<span class="text-slate-300">📋</span>');
        line = line.replace(/📅/g, '<span class="text-slate-300">📅</span>');
        line = line.replace(/💡/g, '<span class="text-slate-300">💡</span>');
        line = line.replace(/✅/g, '<span class="text-slate-200">✅</span>');
        line = line.replace(/❌/g, '<span class="text-slate-200">❌</span>');
        
        // Headings
        if (line.startsWith('### ') || line.startsWith('# ')) {
          const text = line.replace(/^#+ /, '');
          result.push(`<h3 class="text-white font-semibold mt-3 mb-1 border-l-2 border-slate-500 pl-2">${text}</h3>`);
        } else if (line.startsWith('## ')) {
          result.push(`<h2 class="text-white font-bold text-lg mt-4 mb-2">${line.slice(3)}</h2>`);
        }
        // Bullet points
        else if (line.startsWith('- ') || line.startsWith('• ')) {
          result.push(`<li class="ml-4 text-slate-200">${line.slice(2)}</li>`);
        }
        // Numbered lists
        else if (/^\d+\.\s/.test(line)) {
          result.push(`<li class="ml-4 text-slate-200">${line}</li>`);
        }
        // Horizontal rules
        else if (line.trim() === '---') {
          result.push('<hr class="my-3 border-slate-700" />');
        }
        // Blockquotes
        else if (line.startsWith('> ')) {
          result.push(`<blockquote class="border-l-2 border-slate-500 pl-3 my-2 text-slate-300 italic">${line.slice(2)}</blockquote>`);
        }
        // Disclaimer lines
        else if (line.toLowerCase().includes('disclaimer')) {
          result.push(`<p class="my-1 text-slate-400 text-sm">${line}</p>`);
        }
        // AI warning lines
        else if (line.toLowerCase().includes('can make mistakes') || line.toLowerCase().includes('double-check')) {
          result.push(`<p class="my-1 text-slate-400 text-sm">${line}</p>`);
        }
        // Regular paragraph
        else if (line.trim()) {
          result.push(`<p class="my-1 text-slate-200">${line}</p>`);
        } else {
          result.push('<br/>');
        }
      }
    }
    
    // Handle any remaining table buffer
    if (tableBuffer.length > 0) {
      const tableHtml = parseMarkdownTable(tableBuffer);
      if (tableHtml) {
        result.push(tableHtml);
      }
    }
    
    return result.join('')
      .replace(/text-cyan-\d+/g, 'text-white')
      .replace(/text-amber-\d+/g, 'text-white')
      .replace(/text-yellow-\d+/g, 'text-slate-300')
      .replace(/text-orange-\d+/g, 'text-slate-300');
  };

  const stripHtml = (html) => {
    return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  };

  const sidebarLinks = [
    { icon: <Calculator className="w-4 h-4" />, label: "Tax Calculator", path: "/calculator" },
    { icon: <Scale className="w-4 h-4" />, label: "Compare Acts", path: "/compare" },
    { icon: <FileText className="w-4 h-4" />, label: "Tax Slabs", path: "/compare" },
  ];

  const popularTopics = [
    "Section 80C investments",
    "HRA calculation",
    "Capital gains tax",
    "NPS benefits",
    "Home loan deduction",
    "Medical insurance 80D"
  ];

  // TaxSetu Logo URL (transparent background)
  const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_78cf9280-da7a-4b52-a25e-41321aa4bb13/artifacts/xl6xxrnp_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Collapsible Sidebar */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsSidebarOpen(false)}
            />
            
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-slate-900 border-r border-slate-800 z-50 flex flex-col"
            >
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img 
                    src={TAXSETU_LOGO}
                    alt="TaxSetu Logo" 
                    className="w-8 h-8 object-contain"
                  />
                  <span className="font-heading font-semibold text-white">TaxSetu AI</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setIsSidebarOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="flex-1 p-4 space-y-6 overflow-y-auto">
                {/* Budget 2026 - NEW Highlight */}
                <div>
                  <button
                    onClick={() => {
                      sendMessage("Budget 2026 highlights");
                      setIsSidebarOpen(false);
                    }}
                    className="w-full p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-cyan-500/10 border border-amber-500/30 hover:border-amber-500/50 transition-all text-left flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4 text-amber-400" />
                    <span className="font-medium text-amber-600 dark:text-white text-sm">Budget 2026</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold">NEW</span>
                  </button>
                </div>

                {/* Recent Conversations */}
                <div>
                  <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <History className="w-3 h-3" />
                    Recent Conversations
                  </h4>
                  <div className="space-y-1">
                    {recentChats.length > 0 ? (
                      recentChats.slice(0, 8).map((chat, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 group"
                        >
                          <button
                            onClick={async () => {
                              if (chat.session_id) {
                                setSessionId(chat.session_id);
                                localStorage.setItem("taxsetu_session", chat.session_id);
                                // Load messages for this thread
                                try {
                                  const res = await fetch(`${API}/chat-history/${chat.session_id}`);
                                  if (res.ok) {
                                    const data = await res.json();
                                    const loaded = (data.messages || []).map(m => ({
                                      role: m.role,
                                      content: m.content,
                                    }));
                                    setMessages(loaded);
                                  }
                                } catch (err) {
                                  toast.error('Failed to load conversation');
                                }
                              }
                              setIsSidebarOpen(false);
                            }}
                            className="flex-1 text-left px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors text-sm truncate"
                          >
                            {chat.preview || "Previous chat"}
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await fetch(`${API}/chat-session/${chat.session_id}`, { method: 'DELETE' });
                                setRecentChats(prev => prev.filter(c => c.session_id !== chat.session_id));
                                toast.success('Conversation deleted');
                              } catch (err) {
                                toast.error('Failed to delete');
                              }
                            }}
                            className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                            title="Delete conversation"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500 px-3 py-2">No recent conversations</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Chat Area - Full Width */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header - Full Width */}
        <header className="glass border-b border-slate-800 sticky top-0 z-30">
          <div className="w-full px-4 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setIsSidebarOpen(true)}
                className="text-slate-400 hover:text-white"
                data-testid="menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/')}
                className="text-slate-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              
              <div className="flex items-center gap-2">
                <img 
                  src={TAXSETU_LOGO}
                  alt="TaxSetu Logo" 
                  className="w-8 h-8 object-contain"
                />
                <div>
                  <h1 className="font-heading font-semibold text-white">TaxSetu AI</h1>
                  <p className="text-xs text-slate-500">Your AI Tax Expert for India</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate('/compare')}
                className="text-slate-400 hover:text-white hidden sm:inline-flex"
                data-testid="compare-link"
              >
                <Scale className="w-4 h-4 mr-2" />
                Compare
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate('/calculator')}
                className="text-slate-400 hover:text-white hidden sm:inline-flex"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => navigate('/#newsletter')}
                className="border-emerald-400/60 dark:border-emerald-500/60 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500 hidden sm:inline-flex font-medium"
                data-testid="chat-subscribe-btn"
              >
                <Mail className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden md:inline">Subscribe</span>
              </Button>
              <Button 
                variant="ghost"
                size="icon"
                onClick={clearChat}
                className="text-slate-400 hover:text-red-400"
                data-testid="clear-chat-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* User Profile / Sign In */}
              {isAuthenticated ? (
                <div className="relative group">
                  <button 
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 transition-colors"
                    data-testid="user-menu-btn"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="w-5 h-5 text-slate-400" />
                    )}
                    <span className="text-sm text-white hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                    {user?.tier === 'pro' && <Crown className="w-4 h-4 text-yellow-400" />}
                  </button>
                  
                  {/* Dropdown */}
                  <div className="absolute right-0 top-full mt-1 w-56 bg-slate-800 rounded-lg shadow-xl border border-slate-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="p-3 border-b border-slate-700">
                      <p className="text-sm text-white font-medium">{user?.name}</p>
                      <p className="text-xs text-slate-400">{user?.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${user?.tier === 'pro' ? 'bg-gradient-to-r from-amber-500/20 to-amber-600/20 text-amber-400 border border-amber-500/30' : 'bg-cyan-500/20 text-cyan-400'}`}>
                          {user?.tier === 'pro' ? '✦ Pro' : 'Free'}
                        </span>
                        {user?.tier !== 'pro' && (
                          <button 
                            onClick={() => navigate('/pricing')}
                            className="text-xs text-amber-400 hover:text-amber-300 transition-colors"
                          >
                            Upgrade
                          </button>
                        )}
                      </div>
                    </div>
                    {user?.is_admin && (
                      <button
                        onClick={() => navigate('/admin')}
                        className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2"
                      >
                        <Zap className="w-4 h-4" />
                        Admin Dashboard
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setIsSidebarOpen(true);
                        fetchRecentChats();
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700/50 flex items-center gap-2"
                      data-testid="my-conversations-btn"
                    >
                      <History className="w-4 h-4" />
                      My Conversations
                      {recentChats.length > 0 && (
                        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-semibold">
                          {recentChats.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={logout}
                      className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-slate-700/50 flex items-center gap-2 rounded-b-lg"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setAuthModalTrigger('default');
                    setShowAuthModal(true);
                  }}
                  className="bg-cyan-600 hover:bg-cyan-500 text-white"
                  data-testid="sign-in-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Chat Area - Full Width */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {messages.length === 0 ? (
            /* Empty State - Centered like Gemini */
            <div className="flex-1 flex flex-col items-center justify-center px-4">
              <motion.div 
                className="w-full max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-center mb-8">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <img 
                      src={TAXSETU_LOGO} 
                      alt="TaxSetu" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white mb-2">
                    Namaste! I'm TaxSetu AI
                  </h2>
                  <p className="text-slate-400 whitespace-nowrap">
                    Your AI Tax Expert for India. Ask me anything about Income Tax & GST!
                  </p>
                </div>
                
                {/* Centered Input Box */}
                <div className="w-full">
                  {/* Uploaded File Preview */}
                  {uploadedFile && (
                    <div className="mb-3 inline-flex items-start gap-3 px-3 py-2 bg-slate-800/50 rounded-xl border border-slate-700">
                      {imagePreview ? (
                        /* Compact Image Preview with Thumbnail */
                        <div className="flex items-center gap-3">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="h-16 w-auto max-w-[120px] rounded-lg object-cover"
                            data-testid="image-preview"
                          />
                          <div className="flex flex-col">
                            <p className="text-xs text-slate-400 truncate max-w-[150px]">{uploadedFile.name}</p>
                            <button 
                              onClick={removeFile}
                              className="text-xs text-red-400 hover:text-red-300 transition-colors text-left mt-1"
                              data-testid="remove-image-btn"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* PDF Preview */
                        <div className="flex items-center gap-2">
                          <FileUp className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm text-cyan-400 flex-1 truncate">{uploadedFile.name}</span>
                          <button 
                            onClick={removeFile}
                            className="text-slate-400 hover:text-red-400 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 bg-slate-900/80 border border-slate-700 rounded-2xl px-4 py-2 focus-within:border-slate-600 transition-all">
                    {/* File Upload Button */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-slate-400 hover:text-cyan-400 flex-shrink-0 h-8 w-8"
                      data-testid="attach-btn"
                      title="Attach PDF or Image"
                    >
                      <Paperclip className="w-5 h-5" />
                    </Button>
                    
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onPaste={handlePaste}
                      placeholder="Ask about tax slabs, 80C, HRA, GST, ITC, GSTR... (Ctrl+V to paste image)"
                      className="flex-1 bg-transparent text-white placeholder-slate-500 focus:outline-none py-2"
                      disabled={isLoading}
                      data-testid="chat-input"
                    />
                    
                    <Button
                      onClick={() => sendMessage()}
                      disabled={(!inputValue.trim() && !uploadedFile) || isLoading}
                      className="btn-primary text-white rounded-xl px-4 py-2 h-auto"
                      data-testid="send-btn"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  {/* Professional Footer */}
                  <div className="flex flex-col items-center gap-2 mt-4">
                    <p className="text-[11px] text-slate-500 text-center">
                      TaxSetu AI is in Beta. While we aim for 100% accuracy, please cross-check it.
                    </p>
                    <button
                      onClick={() => setIsFeedbackOpen(true)}
                      className="text-xs text-pink-500 hover:text-pink-400 flex items-center gap-1.5 transition-colors bg-pink-500/5 hover:bg-pink-500/10 px-3 py-1.5 rounded-full border border-pink-500/20"
                      data-testid="chat-feedback-btn"
                    >
                      <Heart className="w-3 h-3" />
                      Share Feedback & get chance for Free ITR-1 Filing
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : (
            /* Messages View */
            <>
              <ScrollArea className="flex-1">
                <div className="w-full px-4 lg:px-8 xl:px-16 py-6">
                  <div className="space-y-4 max-w-4xl mx-auto">
                    <AnimatePresence>
                      {messages.map((msg, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] lg:max-w-[75%] ${msg.role === 'user' ? '' : 'group'}`}>
                          {/* Language Toggle Bar - Only for AI responses with content */}
                          {msg.role === 'assistant' && msg.content && (
                            <div className="flex items-center gap-1 mb-2 px-1">
                              <Globe className="w-3 h-3 text-slate-500" />
                              <div className="flex items-center bg-slate-800/50 rounded-full p-0.5 border border-slate-700/50">
                                {[
                                  { id: 'english', label: 'EN', full: 'English' },
                                  { id: 'hinglish', label: 'Hi-En', full: 'Hinglish' },
                                  { id: 'hindi', label: 'हिं', full: 'Hindi' }
                                ].map((lang) => (
                                  <button
                                    key={lang.id}
                                    onClick={() => translateResponse(index, lang.id)}
                                    disabled={translatingIndex === index}
                                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition-all ${
                                      (msg.currentLanguage || 'hinglish') === lang.id
                                        ? 'bg-cyan-500 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                                    }`}
                                    title={lang.full}
                                  >
                                    {translatingIndex === index ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      lang.label
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div 
                            className={`rounded-2xl px-4 py-3 ${
                              msg.role === 'user' 
                                ? 'chat-message-user rounded-br-sm' 
                                : 'chat-message-ai rounded-bl-sm'
                            }`}
                            data-testid={`message-${msg.role}-${index}`}
                          >
                            {msg.role === 'assistant' ? (
                              msg.content ? (
                                <>
                                  {msg.corrected && (
                                    <div
                                      className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-2.5 py-1.5"
                                      data-testid="message-corrected-badge"
                                      title={msg.correction_reason || "Corrected during TaxSetu audit"}
                                    >
                                      <span>✓</span>
                                      Updated by TaxSetu Audit Team
                                    </div>
                                  )}
                                  <div 
                                    className="ai-response text-slate-200 text-sm leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: formatMessage(stripChips(msg.content)) }}
                                  />
                                  {/* Inline calculation form (if AI emitted [[FORM: id]]) */}
                                  {(() => {
                                    const fid = extractFormId(msg.content);
                                    if (!fid) return null;
                                    return (
                                      <InlineCalcForm
                                        formId={fid}
                                        disabled={isLoading}
                                        onSubmit={(text) => sendMessage(text)}
                                      />
                                    );
                                  })()}
                                  {/* Interactive option chips */}
                                  {(() => {
                                    const chips = extractChips(msg.content);
                                    if (!chips.length) return null;
                                    return (
                                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`chips-${index}`}>
                                        {chips.map((chip, ci) => (
                                          <button
                                            key={ci}
                                            onClick={() => sendMessage(chip)}
                                            disabled={isLoading}
                                            className="text-[13px] font-medium px-3.5 py-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 hover:border-cyan-400 hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                            data-testid={`chip-${index}-${ci}`}
                                          >
                                            {chip}
                                          </button>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                </>
                              ) : (
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                                    <span className="text-cyan-400 text-sm font-medium">{loadingStatus || 'Starting...'}</span>
                                  </div>
                                  <div className="text-slate-500 text-xs">
                                    TaxSetu AI is analyzing your query...
                                  </div>
                                </div>
                              )
                            ) : (
                              <div>
                                {/* Show attached image if present */}
                                {msg.image && (
                                  <div className="mb-2">
                                    <img 
                                      src={msg.image} 
                                      alt="Attached" 
                                      className="max-h-48 rounded-lg object-contain"
                                    />
                                  </div>
                                )}
                                <p className="text-white text-sm">{msg.content}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Language Toggle & Share Options for AI responses */}
                          {msg.role === 'assistant' && msg.content && (
                            <div className="mt-3 pt-3 border-t border-slate-700/50 dark:border-slate-700/50">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                {/* Language Toggle */}
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-slate-600 dark:text-slate-400 mr-1">View in:</span>
                                  <button
                                    onClick={() => {
                                      if (msg.currentLanguage !== 'english') {
                                        translateResponse(index, 'english');
                                      }
                                    }}
                                    disabled={translatingIndex === index}
                                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all border ${
                                      msg.currentLanguage === 'english' || !msg.currentLanguage
                                        ? 'bg-cyan-500/30 text-cyan-700 dark:text-cyan-300 border-cyan-500/50 shadow-sm shadow-cyan-500/20'
                                        : 'bg-slate-200/50 dark:bg-slate-700/30 text-slate-600 dark:text-slate-400 hover:bg-cyan-500/20 hover:text-cyan-600 dark:hover:text-cyan-400 border-slate-400/30 dark:border-slate-600/30 hover:border-cyan-500/30'
                                    }`}
                                    data-testid={`lang-en-${index}`}
                                  >
                                    {translatingIndex === index ? '...' : (msg.currentLanguage === 'english' || !msg.currentLanguage ? 'EN ✓' : 'EN')}
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (msg.currentLanguage !== 'hinglish') {
                                        translateResponse(index, 'hinglish');
                                      }
                                    }}
                                    disabled={translatingIndex === index}
                                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all border ${
                                      msg.currentLanguage === 'hinglish'
                                        ? 'bg-amber-500/30 text-amber-700 dark:text-amber-300 border-amber-500/50 shadow-sm shadow-amber-500/20'
                                        : 'bg-slate-200/50 dark:bg-slate-700/30 text-slate-600 dark:text-slate-400 hover:bg-amber-500/20 hover:text-amber-600 dark:hover:text-amber-400 border-slate-400/30 dark:border-slate-600/30 hover:border-amber-500/30'
                                    }`}
                                    data-testid={`lang-hien-${index}`}
                                  >
                                    {translatingIndex === index ? '...' : (msg.currentLanguage === 'hinglish' ? 'Hi-En ✓' : 'Hi-En')}
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (msg.currentLanguage !== 'hindi') {
                                        translateResponse(index, 'hindi');
                                      }
                                    }}
                                    disabled={translatingIndex === index}
                                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all border ${
                                      msg.currentLanguage === 'hindi'
                                        ? 'bg-orange-500/30 text-orange-700 dark:text-orange-300 border-orange-500/50 shadow-sm shadow-orange-500/20'
                                        : 'bg-slate-200/50 dark:bg-slate-700/30 text-slate-600 dark:text-slate-400 hover:bg-orange-500/20 hover:text-orange-600 dark:hover:text-orange-400 border-slate-400/30 dark:border-slate-600/30 hover:border-orange-500/30'
                                    }`}
                                    data-testid={`lang-hi-${index}`}
                                  >
                                    {translatingIndex === index ? '...' : (msg.currentLanguage === 'hindi' ? 'हिंदी ✓' : 'हिंदी')}
                                  </button>
                                </div>
                                
                                {/* Share Options */}
                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => copyToClipboard(msg.content, index)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors text-xs ${
                                      copiedIndex === index
                                        ? 'bg-emerald-500/30 text-emerald-500 dark:text-emerald-400'
                                        : 'bg-slate-500/20 text-slate-600 dark:text-slate-300 hover:bg-slate-500/30'
                                    }`}
                                    data-testid={`copy-answer-${index}`}
                                    title="Copy this answer"
                                  >
                                    {copiedIndex === index ? (
                                      <>
                                        <Check className="w-4 h-4" />
                                        Copied
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="w-4 h-4" />
                                        Copy
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => shareToWhatsApp(msg.content)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors text-xs"
                                    data-testid={`share-whatsapp-${index}`}
                                  >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                    Share
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  <div ref={messagesEndRef} />
                  </div>
                </div>
              </ScrollArea>
              
              {/* Input Area - Bottom (only shown when messages exist) */}
              <div className="glass border-t border-slate-800 p-4">
                <div className="w-full max-w-4xl mx-auto">
                  {/* Uploaded File Preview */}
                  {uploadedFile && (
                    <div className="mb-3 inline-flex items-start gap-3 px-3 py-2 bg-slate-800/50 rounded-xl border border-slate-700">
                      {imagePreview ? (
                        /* Compact Image Preview */
                        <div className="relative flex items-center gap-3">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="h-16 w-auto max-w-[120px] rounded-lg object-cover"
                          />
                          <div className="flex flex-col">
                            <p className="text-xs text-slate-400 truncate max-w-[150px]">{uploadedFile.name}</p>
                            <button 
                              onClick={removeFile}
                              className="text-xs text-red-400 hover:text-red-300 transition-colors text-left mt-1"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* PDF Preview */
                        <div className="flex items-center gap-2">
                          <FileUp className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm text-cyan-400 flex-1 truncate">{uploadedFile.name}</span>
                          <button 
                            onClick={removeFile}
                            className="text-slate-400 hover:text-red-400 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 bg-slate-900/80 border border-slate-700 rounded-2xl px-4 py-2 focus-within:border-slate-600 transition-all">
                    {/* File Upload Button */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-slate-400 hover:text-cyan-400 flex-shrink-0 h-8 w-8"
                      title="Attach Image or PDF (or Ctrl+V to paste)"
                    >
                      <Paperclip className="w-5 h-5" />
                    </Button>
                    
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onPaste={handlePaste}
                      placeholder="Ask a follow-up question... (Ctrl+V to paste image)"
                      className="flex-1 bg-transparent text-white placeholder-slate-500 focus:outline-none py-2"
                      disabled={isLoading}
                    />
                    
                    <Button
                      onClick={() => sendMessage()}
                      disabled={(!inputValue.trim() && !uploadedFile) || isLoading}
                      className="btn-primary text-white rounded-xl px-4 py-2 h-auto"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  {/* Feedback link */}
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <button
                      onClick={() => setIsFeedbackOpen(true)}
                      className="text-xs text-pink-500 hover:text-pink-400 flex items-center gap-1 transition-colors"
                      data-testid="chat-feedback-btn-2"
                    >
                      <Heart className="w-3 h-3" />
                      Share Feedback & get chance for Free ITR-1 Filing
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        trigger={authModalTrigger}
        queriesUsed={modalQueryInfo.used}
        maxQueries={modalQueryInfo.max}
      />
      
      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={isFeedbackOpen} 
        onClose={() => setIsFeedbackOpen(false)} 
      />
      
      {/* Query Limit Modal - Conversion Funnel */}
      <QueryLimitModal
        isOpen={showQueryLimitModal}
        onClose={() => {
          setShowQueryLimitModal(false);
          setPendingQuery(null);
        }}
        type={queryLimitType}
        onGoogleLogin={loginWithGoogle}
        queriesUsed={modalQueryInfo.used}
        maxQueries={modalQueryInfo.max}
      />
    </div>
  );
};

export default ChatPage;
